'use client'

import classnames from 'classnames'
import styles from './PhotoTextCarousel.module.scss'
import PhotoTextCarouselItem from '@/sections/PhotoTextCarousel/PhotoTextCarouselItem/PhotoTextCarouselItem'
import 'swiper/css'
import Swiper from 'swiper'
import { SwiperOptions } from 'swiper/types'
import { useEffect, useRef, useState } from 'react'
import useWindowResize from '@/hooks/use-window-resize'
import Widont from '@/components/Widont/Widont'
import AnimateContainer from '@/components/AnimateContainer/AnimateContainer'
import Icon from '@/components/Icon/Icon'

const PhotoTextCarousel = ({ className, title, items }: SanityPhotoTextCarousel) => {
  const carousel = useRef<Swiper | null>(null)
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const resizeKey = useWindowResize()
  const [activeIndex, setActiveIndex] = useState(0)
  const [isInitialized, setIsInitialized] = useState(false)

  const isFirst = activeIndex === 0
  const isLast = activeIndex === (items?.length ?? 1) - 1

  const goToPrev = () => {
    if (!isFirst) setActiveIndex(activeIndex - 1)
  }

  const goToNext = () => {
    if (!isLast) setActiveIndex(activeIndex + 1)
  }

  useEffect(() => {
    if (!carousel.current || !isInitialized) return
    carousel?.current?.slideTo(activeIndex)
  }, [activeIndex, isInitialized])

  useEffect(() => {
    if (!carouselRef.current) return

    if (carousel.current) {
      carousel.current.destroy()
      setIsInitialized(false)
    }

    const settings: SwiperOptions = {
      slidesPerView: 'auto',
      spaceBetween: 40,
      on: {
        init: function () {
          setIsInitialized(true)
        },
      },
    }

    carousel.current = new Swiper(carouselRef.current, settings)

    carousel.current.on('slideChange', function () {
      if (!carousel.current) return
      setActiveIndex(carousel.current?.activeIndex)
    })

    return () => {
      if (carousel.current) {
        carousel.current.destroy()
      }
    }
  }, [resizeKey])

  if (!title || !items?.length) return null

  return (
    <div className={classnames(styles.PhotoTextCarousel, className)}>
      <AnimateContainer
        animationType="fadeIn"
        className={styles.inner}
      >
        <div className={styles.header}>
          <h2 className={styles.title}>
            <Widont text={title} />
          </h2>
          <div className={styles.navArrows}>
            <button
              className={classnames(styles.navArrow, styles.navArrowPrev, {
                [styles.navArrowDisabled]: isFirst,
              })}
              onClick={goToPrev}
              disabled={isFirst}
              aria-label="Previous slide"
            >
              <Icon name="caretRight" className={styles.navArrowIcon} />
            </button>
            <button
              className={classnames(styles.navArrow, styles.navArrowNext, {
                [styles.navArrowDisabled]: isLast,
              })}
              onClick={goToNext}
              disabled={isLast}
              aria-label="Next slide"
            >
              <Icon name="caretRight" className={styles.navArrowIcon} />
            </button>
          </div>
        </div>
        <div
          className={styles.carouselContainer}
          ref={carouselRef}
        >
          <ul className={classnames('swiper-wrapper', styles.listWrapper)}>
            {items.map((item, i) => (
              <li
                key={i}
                className={classnames('swiper-slide', styles.listWrapper__item)}
              >
                <PhotoTextCarouselItem
                  {...item}
                  className={styles.listWrapper__itemComponent}
                />
              </li>
            ))}
          </ul>
        </div>
      </AnimateContainer>
      <div className={styles.progressBarContainer}>
        <div
          className={styles.progress}
          style={{ width: `${((activeIndex + 1) / items.length) * 100}%` }}
        />
      </div>
    </div>
  )
}

PhotoTextCarousel.displayName = 'PhotoTextCarousel'

export default PhotoTextCarousel
